import { useEffect, useRef, useState } from 'react';
import { ScheduledSms } from '@weave/schema-gen-ts/dist/schemas/messaging/scheduled/shared/v1/models.pb';
import { ThreadItem } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useTranslation } from '@frontend/i18n';
import { createThreadDateList } from '@frontend/thread-body';
import { theme } from '@frontend/theme';
import { SpinningLoader, Text } from '@frontend/design-system';
import { ShareInMessageVirtuosoContext, VirtuosoThreadItem } from '../../types';
import { VirtuosoChatBubble } from './virtuoso-chat-bubble';

type ThreadBodyProps = {
  threadItems: ThreadItem[];
  scheduledMessages: ScheduledSms[];
  context: ShareInMessageVirtuosoContext;
  fetchOlderMessages: () => void;
};

export const ThreadBody = ({ threadItems, context, fetchOlderMessages, ...rest }: ThreadBodyProps) => {
  const { t } = useTranslation('integrated-messaging');
  const [isAtTop, setIsAtTop] = useState(false);
  const ref = useRef<VirtuosoHandle>(null);

  const data = [...createThreadDateList(threadItems)];

  useEffect(() => {
    if (context.hasOlderMessages && isAtTop) fetchOlderMessages();
  }, [context.hasOlderMessages]);

  return (
    <Virtuoso<VirtuosoThreadItem, ShareInMessageVirtuosoContext>
      // This key remounts the virtuoso component when the threadId changes as a way to reset its internal state
      key={context.threadId}
      data={data}
      ref={ref}
      alignToBottom
      followOutput={(isAtBottom) => {
        if (isAtBottom) return context.threadIsLoading ? 'auto' : 'smooth';
        return false;
      }}
      context={context}
      firstItemIndex={100000 - data.length}
      initialTopMostItemIndex={{ index: 'LAST', behavior: 'auto' }}
      itemContent={(_, threadItem, context) => <VirtuosoChatBubble threadItem={threadItem} context={context} />}
      startReached={() => {
        if (context.hasOlderMessages) fetchOlderMessages();
      }}
      atTopStateChange={(isAtTop) => setIsAtTop(isAtTop)}
      components={{
        Footer: ({ context }) => {
          if (context?.isNewThread) {
            return (
              <Text css={{ margin: 0, padding: theme.spacing(2, 0), textAlign: 'center' }}>
                {t(context.isUnknownContactThread ? 'Message will be sent to unknown number' : 'No messages yet.')}
              </Text>
            );
          }

          return null;
        },
        Header: ({ context }) => {
          if (context?.hasOlderMessages || context?.threadIsLoading) {
            return (
              <div css={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(2, 0) }}>
                <SpinningLoader size='medium' />
              </div>
            );
          }

          if (!context?.isNewThread && !context?.hasOlderMessages) {
            return (
              <Text css={{ margin: 0, padding: theme.spacing(2, 0), textAlign: 'center' }}>
                🎉 {t('You have reached the end')}
              </Text>
            );
          }

          return null;
        },
      }}
      {...rest}
    />
  );
};
